import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { recordSessionDevice, sessionDevicesKey } from "../data/sessionQueries";
import { rebindPushSubscription, unsubscribeFromPush } from "../pwa/push";
import { clearAllDrafts } from "../lib/drafts";

/**
 * Session state for the whole application.
 *
 * architecture.md section 4 fixes the rules this file exists to keep:
 *
 * - only the official Supabase client persists auth state;
 * - TanStack Query data is memory-only and is cleared on sign-out, tenant loss, membership
 *   suspension and token failure;
 * - "current device" comes from the JWT `session_id`, never from a stored boolean.
 *
 * Nothing here decides what the user may do. It decides who the server thinks is asking.
 */

export type SessionStatus = "loading" | "signed-in" | "signed-out";

interface SessionContextValue {
  status: SessionStatus;
  session: Session | null;
  userId: string | null;
  /** The JWT `session_id` claim: the identifier of this device's session. */
  authSessionId: string | null;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  /**
   * `local` ends this device only, `others` ends every session except this one, and `global` would
   * revoke every session the user has anywhere.
   *
   * Revised 2026-09-15: ordinary sign-out is local. `app/auth/reauthenticate.ts` states the
   * principle this follows: "Ending somebody's session on their phone because they typed their own
   * password on their laptop is the opposite of what this is for." Ending somebody's phone session
   * because they pressed Logga ut on their laptop is the same mistake.
   *
   * `others` is the deliberate action behind Logga ut andra enheter and the password-reset finish,
   * where this session should survive and every other session should end. No app call currently
   * requests `global`; the type keeps it available only for an explicit future act that says it is
   * ending every device.
   */
  signOut: (scope?: "global" | "local" | "others") => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function readSessionId(session: Session | null): string | null {
  if (!session?.access_token) {
    return null;
  }

  // The claim is read, never trusted for authorisation. Every protected action is re-derived
  // server-side from the same token.
  try {
    const [, payload] = session.access_token.split(".");
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      session_id?: unknown;
    };
    return typeof claims.session_id === "string" ? claims.session_id : null;
  } catch {
    return null;
  }
}

/**
 * The session id and the assurance level the token carries, as one key, since build round 45
 * (2026-10-13). A factor verification keeps the session id and raises `aal1` to `aal2`, and the
 * gateway sends the new-device notice only once the session is a full login, so this tab tells it
 * again at that moment. The server does not depend on it: the operations minute sends the notice from
 * GoTrue's own record of the session if this call never comes.
 */
function readSessionKey(session: Session | null): string | null {
  const sessionId = readSessionId(session);
  if (!sessionId || !session?.access_token) {
    return null;
  }

  try {
    const payload = session.access_token.split(".")[1] ?? "";
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { aal?: unknown };
    return `${sessionId}:${typeof claims.aal === "string" ? claims.aal : "aal1"}`;
  } catch {
    return `${sessionId}:aal1`;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabaseBrowserClient();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");

  /**
   * The `session_id` this tab has already told the database about.
   *
   * A ref rather than state, because nothing renders from it and a re-render on every token refresh
   * would be a re-render of the whole application for a value nothing displays.
   */
  const recordedSessionId = useRef<string | null>(null);

  /**
   * Records the session with `public.record_session_device`, once per session per tab.
   *
   * **Why here and not in a route.** plan.md section 4 requires a session list, and a list is only
   * true if every way a session comes into existence writes to it. There are four:
   * `LoginRoute` signs in with a password, `AuthCallbackRoute` exchanges an email link,
   * `VerificationRoute` answers a factor, and the client refreshes a token on its own with nobody
   * watching. Putting the call in a route would cover the three that have one and miss the fourth,
   * and would miss every future way a session is established besides.
   * `onAuthStateChange` is the one place all four arrive, and `getSession` below is the same event
   * for a tab that opens with a session already stored.
   *
   * **Deduplicated on the session id and its assurance level, not on the event.** A token refresh
   * keeps both; a factor verification keeps the `session_id` and raises the level, and since build
   * round 45 that is a second call, because the new-device notice waits for it. So this fires once
   * per session and level per tab, and the gateway's own `unique (user_id, auth_session_id)` is what
   * makes a second tab of the same session an update rather than a second row.
   *
   * **Nothing waits on it and nothing fails because of it.** `recordSessionDevice` returns false
   * instead of throwing, and the result is discarded here: signing in must not fail because a
   * descriptive row could not be written. What that costs is one row missing from a list, and the
   * mandatory new-login notice going with it, which is stated in migration
   * `20260912000005` rather than left to be discovered.
   */
  const noteSession = useCallback((next: Session | null) => {
    const sessionKey = readSessionKey(next);

    if (!sessionKey || recordedSessionId.current === sessionKey) {
      return;
    }

    recordedSessionId.current = sessionKey;

    /*
      The list is told the row exists, and until 2026-09-13 it was not.

      The write is not awaited by anything, which is right, and nothing refreshed the session list
      after it landed, which was not. `SIGNED_IN` clears the query cache synchronously, `Inställningar`
      then reads the list, and whichever of the two requests finished first decided what the person
      saw. Playwright found the losing order on all three viewports: a list of the account's earlier
      sessions with no row for this one, so no row carried "Den här inloggningen". A person who opens
      settings straight after signing in is exactly the person checking that list, and it showed them
      everywhere they were signed in except here. Invalidating on success makes the list follow the
      write rather than race it.
    */
    void recordSessionDevice().then((recorded) => {
      if (recorded) {
        void queryClient.invalidateQueries({ queryKey: sessionDevicesKey });
        void rebindPushSubscription();
      }
    });

    /*
      The device's notifications follow the session, since 2026-09-13.

      The server delivers a push only while the session that registered the subscription is alive, so
      a new session on a device that already had notifications turned on has to claim them. The call
      runs after the device row lands, because `register_push_subscription` now copies the server's
      device hash from that row. If the row write fails, the browser remains signed in and the device
      panel will answer from the server that notifications are off rather than trusting its own
      subscription.
    */
  }, [queryClient]);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) {
        return;
      }
      setSession(data.session);
      setStatus(data.session ? "signed-in" : "signed-out");
      noteSession(data.session);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setStatus(nextSession ? "signed-in" : "signed-out");

      // Every one of these means the identity behind the cached rows has changed or gone. Holding
      // the previous tenant's data in memory for even one render is a cross-tenant leak in the
      // interface, whatever the database would have said.
      if (event === "SIGNED_OUT" || event === "USER_UPDATED" || event === "SIGNED_IN") {
        queryClient.clear();
      }

      // Cleared on the way out as well as noted on the way in. GoTrue mints a new `session_id` for
      // every sign-in, so the dedupe above would already let the next one through; this is the
      // belt to that pair of braces, and it keeps the ref's meaning exact rather than approximately
      // right. A signed-out tab has told the database about no session, and that is what it holds.
      if (nextSession === null) {
        recordedSessionId.current = null;
      } else {
        noteSession(nextSession);
      }
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [supabase, queryClient, noteSession]);

  const signIn = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined
      });

      if (error) {
        // Account-enumeration-safe: the caller shows one message for every failure shape.
        throw error;
      }
    },
    [supabase]
  );

  const signOut = useCallback(
    async (scope: "global" | "local" | "others" = "local") => {
      /*
        Build round 54, 2026-10-31: this browser's notifications leave with the person. Until then
        sign-out left the push subscription in the browser, still registered to the person who had
        left, and the next person's "Slå på notiser" on the same browser was refused with
        `PORTAL_PUSH_SUBSCRIPTION_CONFLICT`, because the endpoint belonged to another account.

        Started before the session ends, so the revoke can still carry this person's token, and never
        awaited: a sign-out must not wait on a service worker or a network call, and must not fail
        because of one. Not on `others`: this tab's person stays signed in here.
      */
      if (scope !== "others") {
        try {
          void unsubscribeFromPush().catch(() => undefined);
        } catch {
          // A synchronous throw before the promise exists is swallowed the same way.
        }
      }
      await supabase.auth.signOut({ scope });
      queryClient.clear();
      // Drafts are keyed by account, so nobody else would read them, but a shared computer should
      // not keep a person's half-written message past their own sign-out. Added 2026-09-22 with the
      // drafts. Not on `others`: this tab's person is still here and still writing.
      if (scope !== "others") {
        clearAllDrafts();
      }
    },
    [supabase, queryClient]
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      session,
      userId: session?.user?.id ?? null,
      authSessionId: readSessionId(session),
      signIn,
      signOut
    }),
    [status, session, signIn, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside SessionProvider.");
  }
  return value;
}
